import { ArrowDropDown } from "@mui/icons-material"
import {
  Box,
  Divider,
  Drawer,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { ExportRequestType } from "@tc/graphql-server"
import { Button, ButtonMenu, FormDatePicker } from "@tc/ui-dfe/components"
import { MPI_COLOR } from "@tc/ui-dfe/components/ThemeProvider/color-options"
import { useViewAllRequest } from "@tc/ui-dfe/hooks"
import { CloseIcon } from "@tc/ui-dfe/icons"
import { ExportDataMenuOptions } from "@tc/ui-dfe/utils/constants"
import { usePermission } from "@tc/ui-permission/hooks"
import { FEATURE_FLAGS, ROLE } from "@tc/ui-permission/utils"
import { useState } from "react"

export const ExportRequestData = () => {
  const theme = useTheme()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const onCloseDrawer = () => setIsDrawerOpen(false)
  const onOpenDrawer = () => setIsDrawerOpen(true)
  const titleSx = { fontSize: 18, lineHeight: 1.55, mb: 0.5 }
  const buttonSx = { fontSize: 16, lineHeight: 1.5, px: 2.5 }
  const {
    control,
    onSubmit,
    clearAll,
    exportDataDownloadHandler,
    requestsCount,
  } = useViewAllRequest({
    onCloseDrawer,
  })
  const { hasPermission, hasAccess } = usePermission()
  const isAppHytoEnabled = hasPermission([FEATURE_FLAGS.FEATURE_FLAG_APPHYTO])
  const isOCAUser = hasAccess([ROLE.OCABorder])

  const onItemSelected = (id: string) => {
    exportDataDownloadHandler(id as ExportRequestType)
  }

  const renderExportButton = () => {
    if (isOCAUser) {
      return (
        <Button
          ariaLabel="Export Data"
          variant="outlined"
          data-testid="oca-export-data-button-testid"
          onClick={() => {
            exportDataDownloadHandler("OCA_BORDER" as ExportRequestType)
          }}
          sx={{ borderColor: MPI_COLOR.mud }}
          disabled={requestsCount === 0}
        >
          Export Data
        </Button>
      )
    }

    if (isAppHytoEnabled) {
      return (
        <ButtonMenu
          variant="outlined"
          endIcon={<ArrowDropDown />}
          ariaLabel="Export Data"
          title="Export Data"
          items={ExportDataMenuOptions}
          onItemSelected={onItemSelected}
          buttonProps={{ borderColor: MPI_COLOR.mud }}
          disabled={requestsCount === 0}
        />
      )
    }

    return (
      <Button
        ariaLabel="export data"
        variant="outlined"
        color="secondary"
        data-testid="export-data-button-testid"
        sx={{
          "& .MuiButton-endIcon": {
            width: "auto",
            lineHeight: { xs: 2, md: 3 },
          },
        }}
        onClick={onOpenDrawer}
      >
        Export data
      </Button>
    )
  }

  return (
    <>
      {renderExportButton()}
      <Drawer
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 375 },
            p: 3.5,
            gap: 2,
          },
        }}
        anchor="right"
        open={isDrawerOpen}
        onClose={onCloseDrawer}
      >
        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{
            display: "flex",
            height: "100%",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack direction="row" gap={1} alignItems="start">
            <Typography
              variant="h2"
              sx={{ fontSize: { xs: 24 }, lineHeight: 1.33 }}
            >
              Export Data
            </Typography>
            <Button
              ariaLabel="close-button"
              sx={{ ml: "auto", p: 0, minWidth: 24, width: 24, height: 24 }}
              disableRipple
              disableFocusRipple
              onClick={onCloseDrawer}
            >
              <CloseIcon
                sx={{ "&.MuiSvgIcon-root": { width: 24, height: 24 } }}
              />
            </Button>
          </Stack>

          <Divider />

          <Stack>
            <Typography
              variant="h3"
              sx={{ fontSize: { xs: 20 }, lineHeight: 1.33 }}
            >
              Created date range filter
            </Typography>
          </Stack>

          <Stack mb={2}>
            <Typography variant="h4" sx={titleSx}>
              Beginning date
            </Typography>
            <FormDatePicker name="startDate" control={control} />
          </Stack>

          <Stack>
            <Typography variant="h4" sx={titleSx}>
              End date
            </Typography>
            <FormDatePicker name="endDate" control={control} />
          </Stack>

          <Stack direction="row" justifyContent="space-between" mt="auto">
            <Button
              sx={{
                ...buttonSx,
                fontWeight: 600,
                color: theme.palette.error.main,
              }}
              ariaLabel="clear-all"
              variant="text"
              color="warning"
              onClick={clearAll}
            >
              Clear all
            </Button>
            <Button
              sx={buttonSx}
              ariaLabel="download-csv"
              variant="contained"
              type="submit"
            >
              Download CSV
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  )
}
